/**
 * Shared file validator used by BOTH the drag-drop and the file-picker paths
 * in `RecordingUpload`. Keep them in sync — see CLAUDE.md, "File upload
 * validation must apply to ALL upload paths."
 *
 * Mirrors the backend default: `LVDR_RECORDINGS_MAX_BYTES = 500 MiB`. The
 * extension allowlist mirrors what `internal/recordings/sniff.go` accepts
 * after server-side magic-byte sniffing (the server is still the source of
 * truth; this is just a fast UX rejection).
 */

const MAX_BYTES = 500 * 1024 * 1024; // 500 MiB

const ALLOWED_EXTS = new Set<string>([
  '.mp3',
  '.m4a',
  '.wav',
  '.ogg',
  '.flac',
  '.mp4',
  '.webm',
  '.mov',
]);

export const RECORDING_ACCEPTED_EXTS = ALLOWED_EXTS;
export const RECORDING_ACCEPTED_TYPES = Array.from(ALLOWED_EXTS).join(',');
export const RECORDING_MAX_BYTES = MAX_BYTES;

/**
 * Returns `null` if the file is acceptable, otherwise a human-readable error
 * message. Callers should toast the returned message on failure.
 */
export function validateFile(file: File): string | null {
  const lowerName = file.name.toLowerCase();
  const dotIdx = lowerName.lastIndexOf('.');
  const ext = dotIdx === -1 ? '' : lowerName.slice(dotIdx);

  if (!ext || !ALLOWED_EXTS.has(ext)) {
    return ext ? `Unsupported file type: ${ext}` : 'Unsupported file type';
  }

  if (file.size === 0) {
    return 'File is empty';
  }

  if (file.size > MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return `File too large (${mb} MB > 500 MB)`;
  }

  return null;
}
